import { Link, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import MobileMenu from '../../components/feature/MobileMenu';
import { useSEO } from '../../hooks/useSEO';
import JsonLd from '../../components/feature/JsonLd';

export default function ServicesPage() {
  useSEO({
    title: 'Our Services | Design Research, Strategy & Innovation | Human-Centred Solutions',
    description: 'Comprehensive human-centred design services including user research, service design, digital transformation, and innovation strategy for healthcare, government, and social impact organizations.',
    keywords: 'design services, user research, service design, innovation strategy, digital transformation, design thinking workshops, healthcare design, government services',
    canonical: '/services',
    ogType: 'website',
    ogImage: 'https://public.readdy.ai/ai/img_res/773f53e5dac985809eeaf6ea265218f0.jpg'
  });

  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [hash]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": "Human-Centred Design Consultancy",
        "provider": {
          "@type": "Organization",
          "name": "Design For",
          "url": "https://example.com"
        },
        "areaServed": {
          "@type": "Place",
          "name": "Worldwide"
        },
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Design Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Strategy",
                "description": "Creating clarity on where you're going, how you'll grow, and what will set you apart"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Experience Design",
                "description": "Designing future ready experiences that feel intuitive, connected and built around real human needs"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Building Ideas & Capability",
                "description": "Equipping teams with the mindset, tools and confidence to innovate and grow"
              }
            }
          ]
        }
      }} />
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-sm' : 'bg-white'}`}>
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-24">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/511aeba7affe24966a38635cdd087c13.png" 
                alt="Design For" 
                className="h-16"
              />
            </Link>
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Home</Link>
              <Link to="/about" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">About</Link>
              <Link to="/services" className="text-primary hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Services</Link>
              <Link to="/work" className="text-black hover:text-primary transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Work</Link>
              <Link to="/resources" className="text-black hover:text-[#ff4858] transition-colors duration-200 text-sm font-medium uppercase tracking-wider">Insights</Link>
              <Link to="/contact" className="bg-primary text-white px-6 py-3 rounded-full hover:bg-primary/90 transition-all duration-200 text-sm font-bold uppercase tracking-wider whitespace-nowrap">
                Contact
              </Link>
            </div>
            <button 
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden text-black"
            >
              <i className="ri-menu-line text-3xl"></i>
            </button>
          </div>
        </div>
      </nav>

      <MobileMenu isOpen={mobileMenuOpen} onClose={() => setMobileMenuOpen(false)} />

      {/* Hero Section */}
      <section className="pt-40 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/23a175598fa12b79bb0ee264dd2aa402.jpeg"
            alt="Services background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <h1 className="text-7xl lg:text-8xl font-black text-white tracking-tight leading-[0.95] mb-10">
            Services
          </h1>
          <p className="text-2xl lg:text-3xl text-white font-medium leading-relaxed max-w-5xl mb-10">
            Design For helps organisations figure out where they&apos;re going, design better experiences for real people, and build the skills to keep improving long after we&apos;ve left.
          </p>
          <div>
            <Link to="/contact" className="inline-flex items-center gap-3 bg-primary text-white px-10 py-5 rounded-full hover:bg-primary/90 transition-all duration-300 text-base font-bold uppercase tracking-wider whitespace-nowrap group">
              Get Started
              <i className="ri-arrow-right-line text-xl group-hover:translate-x-1 transition-transform"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Core Services */}
      <section className="py-24 bg-[#f8f8f8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="space-y-8">
            {/* Service 01 - Strategy */}
            <div id="strategy" className="group bg-primary rounded-2xl p-10 lg:p-14 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 cursor-default">
              <div className="flex flex-col lg:flex-row gap-10 items-stretch">
                <div className="space-y-8 flex-1">
                  <div className="space-y-3">
                    <p className="text-sm font-black text-white/50 tracking-wider">01</p>
                    <div className="flex items-center gap-4">
                      <h3 className="text-4xl lg:text-5xl font-black text-white tracking-tight">Strategy</h3>
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                        <i className="ri-line-chart-line text-white/60 group-hover:text-white/90 text-4xl transition-colors duration-300"></i>
                      </div>
                    </div>
                    <p className="text-xl text-white/90 font-medium leading-relaxed max-w-3xl">
                      Creating clarity on where you&apos;re going, how you&apos;ll grow, and what will set you apart.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Customer Experience Strategy
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Define how customers should feel and move through your world from first touch to long-term loyalty.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Human Centred AI Strategy
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Define where AI can create meaningful value for people, using deep human insight to shape how intelligent AI technologies support better experiences.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Vision & Northstar
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Set a clear, compelling direction that aligns teams and guides better decisions every day.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Value Propositions
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Clarify why you matter and what makes you the obvious choice.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Business Models
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Design how value is created, delivered and captured in a way that drives sustainable growth.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Future Forecasting
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Explore what&apos;s coming next and translate emerging shifts into confident action today.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                    <Link to="/contact" className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider border-b border-white/40 hover:border-white pb-0.5 transition-colors">
                      Work with us <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[480px] xl:w-[540px]">
                  <div className="w-full h-96 lg:h-full min-h-[420px] rounded-xl overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/59ca997d5d37a61c379ae60ca4f40e99.jpeg"
                      alt="Strategy workshop"
                      className="w-full h-full object-cover object-top scale-100 group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service 02 - Experience Design */}
            <div id="experience-design" className="group bg-primary rounded-2xl p-10 lg:p-14 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 cursor-default">
              <div className="flex flex-col lg:flex-row gap-10 items-stretch">
                <div className="space-y-8 flex-1">
                  <div className="space-y-3">
                    <p className="text-sm font-black text-white/50 tracking-wider">02</p>
                    <div className="flex items-center gap-4">
                      <h3 className="text-4xl lg:text-5xl font-black text-white tracking-tight">Experience Design</h3>
                      <div className="w-12 h-12 flex items-center justify-center flex-shrink-0">
                        <i className="ri-team-line text-white/60 group-hover:text-white/90 text-4xl transition-colors duration-300"></i>
                      </div>
                    </div>
                    <p className="text-xl text-white/90 font-medium leading-relaxed max-w-3xl">
                      Designing future ready experiences that feel intuitive, connected and built around real human needs.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Service Design
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Shape how your services work end-to-end so they feel seamless, consistent and genuinely helpful.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        User Research
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Spend time with real people to deeply understand their world, so what you create truly fits their lives.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Journey Mapping
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Map today&apos;s reality and design tomorrow&apos;s future state, creating experiences that are clearer, smarter and more meaningful.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Ideation & Co-Design
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Bring teams and users together to imagine bold possibilities and turn them into practical, usable solutions.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Customer Testing & Experimentation
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Test and refine ideas early so you launch with confidence and build experiences that truly land.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        AI-Enabled Experience Design
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Design how AI shows up across real customer experiences, turning human insight into intelligent services that feel intuitive, trusted and genuinely helpful.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                    <Link to="/contact" className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider border-b border-white/40 hover:border-white pb-0.5 transition-colors">
                      Work with us <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[480px] xl:w-[540px]">
                  <div className="w-full h-96 lg:h-full min-h-[420px] rounded-xl overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/63681d5d3cbfa151a1002be71469addf.jpeg"
                      alt="Experience Design"
                      className="w-full h-full object-cover object-top scale-100 group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Service 03 - Building Ideas & Capability */}
            <div id="building-ideas" className="group bg-primary rounded-2xl p-10 lg:p-14 hover:shadow-2xl hover:-translate-y-1 transition-all duration-500 cursor-default">
              <div className="flex flex-col lg:flex-row gap-10 items-stretch">
                <div className="space-y-8 flex-1">
                  <div className="space-y-3">
                    <p className="text-sm font-black text-white/50 tracking-wider">03</p>
                    <div className="flex items-center gap-4">
                      <h3 className="text-4xl lg:text-5xl font-black text-white tracking-tight">Building Ideas & Capability <i className="ri-lightbulb-flash-line text-white/60 group-hover:text-white/90 text-4xl transition-colors duration-300"></i></h3>
                    </div>
                    <p className="text-xl text-white/90 font-medium leading-relaxed max-w-3xl">
                      Equipping teams with the mindset, tools and confidence to innovate and grow.
                    </p>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Innovation Incubators
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Turn bold ideas into tangible, testable solutions through focused, high-energy programmes.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Toolkits & Training
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Provide practical tools and hands-on learning that teams can apply immediately.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Bootcamps & Design Jams
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Create fast-paced, collaborative sessions that unlock creativity and generate real momentum.
                      </p>
                    </div>
                    <div className="space-y-2">
                      <h4 className="text-base font-black text-white flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-white/50 rounded-full flex-shrink-0 group-hover:bg-white transition-colors duration-300"></span>
                        Human Centred AI Capability
                      </h4>
                      <p className="text-white/75 text-sm font-medium leading-relaxed pl-4">
                        Equip teams with the mindset and tools to turn human insight into AI-powered ideas, services and solutions that scale responsibly and deliver real impact.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-400">
                    <Link to="/contact" className="inline-flex items-center gap-2 text-white text-sm font-bold uppercase tracking-wider border-b border-white/40 hover:border-white pb-0.5 transition-colors">
                      Work with us <i className="ri-arrow-right-line"></i>
                    </Link>
                  </div>
                </div>
                <div className="flex-shrink-0 w-full lg:w-[480px] xl:w-[540px]">
                  <div className="w-full h-96 lg:h-full min-h-[420px] rounded-xl overflow-hidden">
                    <img
                      src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/4ad0a90dbe5e416105555825b8b1142f.jpeg"
                      alt="Building Ideas & Capability workshop"
                      className="w-full h-full object-cover object-top scale-100 group-hover:scale-105 transition-transform duration-700"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-14">
            <Link to="/contact" className="bg-primary text-white px-12 py-5 rounded-full hover:bg-primary/90 transition-all duration-300 text-base font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap">
              Get Started
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Challenges I Solve */}
      <section className="py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <h2 className="text-6xl lg:text-7xl font-black text-black tracking-tight mb-16">Challenges we solve</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            {/* Challenge 1 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;Our experience for customers isn&apos;t good enough & we need to fix it.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Let&apos;s redesign complex journeys and services so they&apos;re easier, clearer and more supportive for the people who rely on them.
              </p>
            </div>

            {/* Challenge 2 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;We&apos;re not truly listening to our users and don&apos;t know who they are.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Let&apos;s uncover deep human insight through research that guides smarter decisions and unlocks new opportunities.
              </p>
            </div>

            {/* Challenge 3 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;We have ideas, but we don&apos;t know which ones are worth investing in.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Let&apos;s run rapid research, prototyping and sprints to test and validate concepts before they go too far.
              </p>
            </div>

            {/* Challenge 4 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;We need a vision and strategy that people across our organisation can actually get behind.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                We help leaders define a compelling, actionable future state and align teams around it.
              </p>
            </div>

            {/* Challenge 5 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;We need to innovate and evolve, but our company isn&apos;t built for speed.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                Let&apos;s introduce ways of working, behaviours and tools that help teams move faster and with confidence.
              </p>
            </div>

            {/* Challenge 6 */}
            <div className="group bg-[#f8f8f8] rounded-2xl p-8 hover:shadow-lg hover:-translate-y-1 border border-transparent hover:border-primary/20 transition-all duration-300 cursor-default">
              <div className="w-0 group-hover:w-8 h-0.5 bg-primary mb-4 transition-all duration-300"></div>
              <p className="text-lg font-black text-black mb-3 group-hover:text-primary transition-colors duration-300">
                &quot;We need to future proof our business and design the experience of tomorrow.&quot;
              </p>
              <p className="text-sm text-gray-500 font-medium leading-relaxed">
                We help organisations understand what&apos;s coming next &amp; turn it into future ready business models, value propositions and experiences.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Ways to Get Started */}
      <section className="py-28 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/53703d9b542b2cc694ffe0ba633452a9.jpeg"
            alt="Ways to get started background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-black/30"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
          <h2 className="text-6xl lg:text-7xl font-black text-white tracking-tight mb-16">Ways to get started&nbsp;</h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Way 01 */}
            <div className="group bg-white rounded-2xl p-8 space-y-4 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
              <p className="text-4xl font-black text-primary group-hover:scale-110 inline-block transition-transform duration-300 origin-left">01</p>
              <h3 className="text-2xl font-black text-black">Workshops</h3>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">3 hours - 3 days</p>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                High energy, productive and collaborative sessions with a specific goal and focus. This is a great place to start to align, gain momentum and inject high value quickly.
              </p>
              <div className="pt-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <Link to="/contact" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
                  Get started <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Way 02 */}
            <div className="group bg-white rounded-2xl p-8 space-y-4 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
              <p className="text-4xl font-black text-primary group-hover:scale-110 inline-block transition-transform duration-300 origin-left">02</p>
              <h3 className="text-2xl font-black text-black">Innovation Sprints</h3>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">1 - 8 weeks</p>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Moving from ideas to execution in the leanest, fastest way. We repeat the design process until we have a concept or initiative that works.
              </p>
              <div className="pt-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <Link to="/contact" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
                  Get started <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Way 03 */}
            <div className="group bg-white rounded-2xl p-8 space-y-4 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
              <p className="text-4xl font-black text-primary group-hover:scale-110 inline-block transition-transform duration-300 origin-left">03</p>
              <h3 className="text-2xl font-black text-black">Big impact</h3>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">3 - 12 months</p>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Bespoke approach using design methods to cut through complexity to create strategies and services. Here we embed change, scale, and deliver long term results.
              </p>
              <div className="pt-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <Link to="/contact" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
                  Get started <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Way 04 */}
            <div className="group bg-white rounded-2xl p-8 space-y-4 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
              <p className="text-4xl font-black text-primary group-hover:scale-110 inline-block transition-transform duration-300 origin-left">04</p>
              <h3 className="text-2xl font-black text-black">Training & toolkits</h3>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">3 hours - 3 days</p>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Education for all levels and backgrounds. A great way to upskill and provide practical tools that add value to any business or team.
              </p>
              <div className="pt-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <Link to="/contact" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
                  Get started <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>

            {/* Way 05 */}
            <div className="group bg-white rounded-2xl p-8 space-y-4 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 cursor-default">
              <p className="text-4xl font-black text-primary group-hover:scale-110 inline-block transition-transform duration-300 origin-left">05</p>
              <h3 className="text-2xl font-black text-black">Consulting &amp; Advisory</h3>
              <p className="text-xs uppercase tracking-wider text-gray-400 font-bold">1 day - 6 months</p>
              <p className="text-sm text-gray-500 leading-relaxed font-medium">
                Using our capabilities and experience to lead, be part of a team, create outputs and provide advice.
              </p>
              <div className="pt-2 opacity-0 group-hover:opacity-100 translate-y-1 group-hover:translate-y-0 transition-all duration-300">
                <Link to="/contact" className="inline-flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
                  Get started <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>

          <div className="text-center mt-14">
            <Link to="/contact" className="bg-primary text-white px-12 py-5 rounded-full hover:bg-primary/90 transition-all duration-300 text-base font-bold uppercase tracking-wider inline-flex items-center gap-3 whitespace-nowrap">
              Get Started
              <i className="ri-arrow-right-line text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid md:grid-cols-3 gap-12 mb-16">
            <div className="space-y-6">
              <img 
                src="https://static.readdy.ai/image/7afcb539829d8ddcd28f62262d25b133/177dc540d7c9a6700435609d623348f7.png" 
                alt="Design For" 
                className="h-16"
              />
              <p className="text-white/60 text-sm leading-relaxed">Strategic consulting for ambitious businesses.</p>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Navigate</h4>
              <div className="space-y-3">
                <Link to="/" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Home</Link>
                <Link to="/about" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">About</Link>
                <Link to="/services" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Services</Link>
                <Link to="/work" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Work</Link>
                <Link to="/resources" className="block text-white/70 hover:text-[#ff4858] transition-colors text-sm font-medium">Insights</Link>
                <Link to="/contact" className="block text-white/70 hover:text-primary transition-colors text-sm font-medium">Contact</Link>
              </div>
            </div>

            <div>
              <h4 className="text-sm uppercase tracking-wider text-white font-bold mb-6">Follow</h4>
              <div className="flex gap-4">
                <a href="https://www.linkedin.com/in/kara-prosser-26960977/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 bg-white/10 hover:bg-primary rounded-full flex items-center justify-center transition-colors cursor-pointer">
                  <i className="ri-linkedin-fill text-white text-xl"></i>
                </a>
              </div>
            </div>
          </div>

          <div className="border-t border-white/10 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-white/40 font-medium">© 2026 Design For Consulting</p>
            <div className="flex gap-6">
              <Link to="/privacy" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Privacy</Link>
              <Link to="/terms" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Terms</Link>
              <Link to="/cookies" className="text-white/40 hover:text-primary transition-colors text-sm font-medium">Cookies</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}